import React, { useEffect } from 'react';
import { Drawer } from '@dsg-tech/homefield-react';
import { StoreIdentifierName } from 'types';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
  searchDataSelector,
  searchInputSelector,
  showMobileSaytDrawerSelector,
  showPreSaytDrawerSelector,
} from 'store/selectors/saytSelector';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import { setShowPreSaytDrawer } from 'store/slices/sayt';
import SearchBar from '../SearchBar';
import SAYTSuggestions from '../SAYTSuggestions/SAYTSuggestions';
import getSearchUtils from '../SearchUtils';
import SAYTSuggestionsV2 from '../SAYTSuggestionsV2/SAYTSuggestionsV2';
import PreSAYTSuggestions from '../PreSAYTSuggestions/PreSAYTSuggestions';
import './SAYTModalMobile.scss';

type SAYTModalMobileProps = {
  storeIdentifierName: StoreIdentifierName;
  thumbnailApi: string;
};

type SayTContentType = 'preSayt' | 'saytV2' | 'sayt' | 'none';

const SAYTModalMobile = ({ storeIdentifierName, thumbnailApi }: SAYTModalMobileProps) => {
  const dispatch = useAppDispatch();
  const showMobileSaytDrawer = useAppSelector(showMobileSaytDrawerSelector);
  const showPreSaytDrawer = useAppSelector(showPreSaytDrawerSelector);
  const searchInput = useAppSelector(searchInputSelector);
  const searchData = useAppSelector(searchDataSelector);
  const saytRedesignEnabled = useAppSelector(getFeatureFlagByNameSelector('saytRedesignEnabled'));
  const preSaytRedesignEnabled = useAppSelector(
    getFeatureFlagByNameSelector('preSaytRedesignEnabled'),
  );
  const { updateSearchTermSAYT, submitSearch } = getSearchUtils(storeIdentifierName);

  useEffect(() => {
    if (showMobileSaytDrawer) {
      const modal = document.querySelector('.sayt-modal-mobile') as HTMLElement;
      const searchBar = modal?.querySelector('#searchInput') as HTMLInputElement;
      searchBar?.focus();
    }
  }, [showMobileSaytDrawer]);

  const handleShowPreSayt = (value: boolean) => {
    dispatch(setShowPreSaytDrawer(value));
  };

  let contentType: SayTContentType = 'none';

  // Determine the content type based on feature flags and conditions. PreSayt is shown if both preSaytRedesignEnabled and saytRedesignEnabled are true. And presayt has content.
  // If only saytRedesignEnabled is true, show SAYT V2. If neither is true, show SAYT.

  if (saytRedesignEnabled) {
    if (preSaytRedesignEnabled && showPreSaytDrawer) {
      contentType = 'preSayt';
    } else {
      contentType = 'saytV2';
    }
  } else if (searchData?.suggestions && searchInput && searchInput.length > 2) {
    contentType = 'sayt';
  }

  let content;
  switch (contentType) {
    case 'preSayt':
      content = (
        <ErrorBoundaryWithLogger componentId="SAYTModalMobile-PreSAYTSuggestions">
          <PreSAYTSuggestions
            storeIdentifierName={storeIdentifierName}
            handleShowPreSayt={handleShowPreSayt}
          />
        </ErrorBoundaryWithLogger>
      );
      break;
    case 'saytV2':
      content = (
        <ErrorBoundaryWithLogger componentId="SAYTModalMobile-SAYTSuggestionsV2">
          <SAYTSuggestionsV2
            storeIdentifierName={storeIdentifierName}
            searchData={searchData}
            submitSearch={submitSearch}
            searchTerm={searchInput}
            thumbnailApi={thumbnailApi}
            mobileDrawer
          />
        </ErrorBoundaryWithLogger>
      );
      break;
    case 'sayt':
      content = (
        <ErrorBoundaryWithLogger componentId="SAYTModalMobile-SAYTSuggestions">
          <SAYTSuggestions
            storeIdentifierName={storeIdentifierName}
            thumbnailApi={thumbnailApi}
            searchData={searchData}
            submitSearch={submitSearch}
            searchTerm={searchInput}
            updateSearchTermSAYT={updateSearchTermSAYT}
            mobileDrawer
          />
        </ErrorBoundaryWithLogger>
      );
      break;
    default:
      content = null;
      break;
  }

  return (
    <Drawer
      open={showMobileSaytDrawer}
      closable={false}
      className="sayt-modal-mobile"
      size="large"
      onCloseModal={() => {}}>
      <>
        <ErrorBoundaryWithLogger componentId="SAYTModalMobile-SearchBar">
          <SearchBar
            storeIdentifierName={storeIdentifierName}
            thumbnailApi={thumbnailApi}
            mobileDrawer
          />
        </ErrorBoundaryWithLogger>
        {content}
      </>
    </Drawer>
  );
};

export default SAYTModalMobile;
